import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Badge } from '#/modules/commonComponents/badge';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialog2xlMaxWidthClassName,
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { getOutboundSourceTypeMeta } from '#/modules/momMgmt/workOrder/outbound/helper/outboundSourceTypeMeta';

function StatCell({ label, value, emphasize = false }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-base font-semibold',
          emphasize ? 'text-red-600' : 'text-gray-900',
        )}
      >
        {value}
      </p>
    </div>
  );
}

StatCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  emphasize: PropTypes.bool,
};

function placeLabel(source) {
  return [source.warehouseLabel, source.locationLabel].filter(Boolean).join(' / ');
}

/**
 * 複選 candidates sources（不填數量；確認後由父層 sync lines）。
 * 資料僅來自 stock-issue-candidates，不打全倉 inventory list。
 * 開啟時由父層 key remount，以 initialSourceKeys 初始化預選。
 */
const WorkOrderOutboundSourcePickerDialog = ({
  open,
  onOpenChange,
  material,
  initialSourceKeys = [],
  onConfirm,
}) => {
  const sources = useMemo(
    () => material?.sources ?? [],
    [material],
  );
  const [selectedKeys, setSelectedKeys] = useState(
    () => new Set(initialSourceKeys),
  );
  const [candidateKeyword, setCandidateKeyword] = useState('');

  const title = material
    ? `選擇來源 — ${material.mtrName || material.mtrNo || material.mtrId}`
    : '選擇來源';

  const displaySources = useMemo(() => {
    const q = candidateKeyword.trim().toLowerCase();
    if (!q) {
      return sources;
    }

    return sources.filter((source) => {
      const stockNo = source.stockNo ?? source.id ?? '';
      const place = placeLabel(source);
      return [String(stockNo), String(place)].join(' ').toLowerCase().includes(q);
    });
  }, [candidateKeyword, sources]);

  const displaySelectedCount = displaySources
    .filter((source) => selectedKeys.has(source.sourceKey)).length;
  const displayTotal = displaySources.length;
  const allSelected = displayTotal > 0 && displaySelectedCount === displayTotal;
  const someSelected = displaySelectedCount > 0 && displaySelectedCount < displayTotal;
  const selectAllChecked = allSelected ? true : (someSelected ? 'indeterminate' : false);

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      setCandidateKeyword('');
    }
    onOpenChange(nextOpen);
  }

  function handleToggle(sourceKey, checked) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(sourceKey);
      } else {
        next.delete(sourceKey);
      }
      return next;
    });
  }

  function handleSelectAll(checked) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      displaySources.forEach((source) => {
        if (checked === true) {
          next.add(source.sourceKey);
        } else {
          next.delete(source.sourceKey);
        }
      });
      return next;
    });
  }

  function handleConfirm() {
    const selectedSources = sources.filter((source) => selectedKeys.has(source.sourceKey));
    onConfirm(selectedSources);
    onOpenChange(false);
  }

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2">
      <TableSearchInput
        useKeyword
        keyword={candidateKeyword}
        onKeywordChange={setCandidateKeyword}
        placeholder="搜尋來源庫存或儲位"
      />
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description="勾選庫存或待處理來源；使用數量於右側已選取調整。"
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton
      maxWidthClassName={dialog2xlMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={() => handleOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText="確認"
      cancelText="取消"
      confirmDisabled={false}
    >
      <div className="space-y-3">
        <div
          className={cn(
            'grid grid-cols-2 gap-2',
            'sticky top-0 z-10 border-b border-slate-200 bg-panel pb-4',
          )}
        >
          <StatCell
            label="需求"
            value={material?.requiredQty ?? '—'}
          />
          <StatCell
            label="剩餘"
            value={material?.remainingQty ?? '—'}
            emphasize={Number(material?.remainingQty) > 0}
          />
        </div>
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">此物料無可選來源。</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-slate-200 rounded-md border border-slate-200 p-0">
            <li
              className={cn(
                'flex cursor-pointer items-center gap-2 px-3 py-2.5 transition-colors',
                'bg-slate-50 hover:bg-slate-100',
              )}
              onClick={() => handleSelectAll(!allSelected)}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <Checkbox
                  checked={selectAllChecked}
                  onCheckedChange={(value) => handleSelectAll(value === true)}
                  aria-label="全選來源"
                />
              </div>
              <span className="text-sm font-medium text-slate-800">全選</span>
              <span className="text-xs text-muted-foreground">
                已選
                {' '}
                {displaySelectedCount}
                {' '}
                /
                {' '}
                {displayTotal}
              </span>
            </li>
            {displaySources.length === 0 ? (
              <li className="px-3 py-3 text-sm text-muted-foreground">
                無符合篩選條件的來源。
              </li>
            ) : null}
            {displaySources.map((source) => {
              const checked = selectedKeys.has(source.sourceKey);
              const typeMeta = getOutboundSourceTypeMeta(source.sourceType);
              const place = placeLabel(source);

              return (
                <li
                  key={source.sourceKey}
                  role="option"
                  aria-selected={checked}
                  className={cn(
                    'grid cursor-pointer grid-cols-1 gap-2 px-3 py-3 transition-colors',
                    'sm:grid-cols-[auto_minmax(0,1fr)]',
                    'sm:items-start',
                    checked
                      ? 'bg-primary-100 shadow-inner hover:bg-primary-100'
                      : 'hover:bg-slate-50',
                  )}
                  onClick={() => handleToggle(source.sourceKey, !checked)}
                >
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => handleToggle(
                        source.sourceKey,
                        value === true,
                      )}
                      aria-label={`選取 ${source.stockNo || source.id}`}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <Badge
                        variant={typeMeta.variant}
                        className={typeMeta.badgeClassName}
                      >
                        {typeMeta.shortLabel}
                      </Badge>
                      <span className="font-mono text-sm text-slate-800">
                        {source.stockNo || source.id}
                      </span>
                    </div>
                    {place ? (
                      <p className="truncate text-xs text-muted-foreground">{place}</p>
                    ) : null}
                    <div className="mt-1 flex items-baseline gap-2 text-sm">
                      <span className="text-xs text-muted-foreground">可用量</span>
                      <span className="font-medium tabular-nums">{source.qty}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppDialog>
  );
};

WorkOrderOutboundSourcePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  material: PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    requiredQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    remainingQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sources: PropTypes.arrayOf(PropTypes.object),
  }),
  initialSourceKeys: PropTypes.arrayOf(PropTypes.string),
  onConfirm: PropTypes.func.isRequired,
};

export default WorkOrderOutboundSourcePickerDialog;
